import i18n from '../i18n'

/**
 * The tap-a-word note feature (owner request, replacing the practice
 * screen's whole-sentence translation toggle — no go-live-plan item).
 *
 * The shared, load-bearing rule: a sentence's words are `textEn.split(/\s+/)`,
 * and a word note's `index` is that word's 0-based position in that split —
 * not a character offset. Three other places must tokenize identically or a
 * note lands on the wrong word: the backend's admin-lessons validation
 * (`normalizeWordNotes` in `admin-lessons.service.ts`), the backend's
 * defensive read-side parse (`parseWordNotes` in `lessons.service.ts`), and
 * `pages/Admin.jsx`'s per-word inputs, which import `splitWords` from here.
 */

const t = (key, options) => i18n.t(key, { ns: 'review', ...options })

/** A sentence's words, in order — exactly `textEn.split(/\s+/)`, untrimmed. */
export function splitWords(textEn) {
  return String(textEn ?? '').split(/\s+/)
}

/** Shown when a tapped word has no authored note — every word answers something. */
export function noNoteText() {
  return t('wordNotes.noNote')
}

/** The practice screen's popup body: the note for `index`, or the placeholder. */
export function noteForWord(wordNotes, index) {
  const found = (wordNotes ?? []).find((entry) => entry.index === index)
  return found?.note || noNoteText()
}

/**
 * Drops any note whose index no longer exists once a sentence's text has
 * `wordCount` words — the admin panel's answer to "the admin shortened the
 * sentence after typing some notes", without fuzzy word-tracking: simplicity
 * over cleverness for a small content tool.
 */
export function pruneWordNotes(wordNotes, wordCount) {
  return (wordNotes ?? []).filter((entry) => entry.index < wordCount)
}

/**
 * The wire shape for one sentence's `wordNotes`: pruned to the current
 * `textEn`'s word count, trimmed, and empty notes dropped — the same rules
 * the backend enforces, applied client-side so a stray blank input never
 * turns into a rejected request.
 */
export function sanitizeWordNotes(wordNotes, textEn) {
  const wordCount = splitWords(textEn).length
  return pruneWordNotes(wordNotes, wordCount)
    .map((entry) => ({ index: entry.index, note: entry.note.trim() }))
    .filter((entry) => entry.note.length > 0)
}
